import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type { ReportPoisRoutesDto } from "@report/data/dto/report-pois-routes.dto.ts";
import { ReportPoisRoutesDtoSchema } from "@report/data/dto/report-pois-routes.dto.ts";
import { Report } from "@report/domain/models/report.model";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";

@injectable()
export class ReportPoisRoutesMapper {
    map(
        reportPoisRoutesDto: ReportPoisRoutesDto,
    ): Result<Report[], ModelError<typeof Report>> {
        const reportPoisRoutesDtoParsed =
            ReportPoisRoutesDtoSchema.safeParse(reportPoisRoutesDto);

        if (!reportPoisRoutesDtoParsed.success) {
            console.error(reportPoisRoutesDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        const reportsResults = Object.values(reportPoisRoutesDto.data).map(
            (report) => {
                const station = report.station;
                const reportData = report.data;
                const totals = report.totals;

                const reportMapped: Record<string, string | number | null>[] =
                    reportData.map((item) => ({
                        date: item.date,
                        arrivedTime: item.arrived_time ?? 0,
                        destinationPoi: item.destination_poi ?? 0,
                        fromDestinationAt: item.from_destination_at ?? 0,
                        fromOriginAt: item.from_origin_at ?? 0,
                        originPoi: item.origin_poi,
                        poiTime: item.poi_time ?? 0,
                        route: item.route,
                        toDestinationAt: item.to_destination_at ?? 0,
                    }));
                reportMapped.push(totals);

                return Report.createWithRecovery({
                    stationReportSummary: {
                        icon: station.icon,
                        stationId: station.id,
                        stationName: `${station.name} (${station.technical_name})`,
                    },
                    items: reportMapped,
                    pagination: {
                        count: 0,
                        current: 0,
                        pages: 0,
                        perPage: 0,
                    },
                });
            },
        );

        if (reportsResults.some((result) => result.isErr())) {
            return err(new NonRecuperableModelError());
        }

        return ok(
            reportsResults
                .map((result) => result.unwrapOr(null))
                .filter(Boolean),
        );
    }
}
